import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Carousel,
  Radio,
  theme,
  Grid,
  List,
  Drawer,
  Modal,
  Tag,
  Divider,
  Image,
  Empty,
  Spin,
  Flex,
  message,
} from "antd";
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  StarFilled,
  UserOutlined,
  CalendarOutlined,
  ColumnWidthOutlined,
  PictureOutlined,
  PhoneOutlined,
  CopyOutlined,
} from "@ant-design/icons";

// سرویس‌ها و تایپ‌ها
import { getNoticesOData, NoticeHeaderDto } from "api/services/odata/noticesOdataService";
import { getMediaByAlbumId, MediaItemDto } from "api/services/mediaService";
import { getUserPhoneNumber } from "api/services/accountService";
import { getRelativeTime } from "utils/timeHelper";

const { Title, Text, Paragraph } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

const TOP = 20;

const goldenAnimationStyles = `
  @keyframes goldenFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  .ad-card-gold-wrapper {
    background: linear-gradient(120deg, #f59e0b, #fbbf24, #fef08a, #d97706, #f59e0b);
    background-size: 300% 300%;
    animation: goldenFlow 5s ease infinite;
    padding: 2px;
    border-radius: 14px;
    box-shadow: 0 4px 15px rgba(245, 158, 11, 0.22);
  }
`;

const NoticeList: React.FC = () => {
  const { token } = useToken();
  const screens = useBreakpoint();

  const [notices, setNotices] = useState<NoticeHeaderDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [skip, setSkip] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<"masterDetail" | "grid">("masterDetail");

  const [selectedNotice, setSelectedNotice] = useState<NoticeHeaderDto | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState<boolean>(false);
  const [isGridModalOpen, setIsGridModalOpen] = useState<boolean>(false);

  // وضعیت نمایش و دریافت شماره تلفن
  const [showPhone, setShowPhone] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [isPhoneLoading, setIsPhoneLoading] = useState<boolean>(false);

  // کش تصاویر آلبوم‌ها
  const [mediaMap, setMediaMap] = useState<Record<string, MediaItemDto[]>>({});

  // فیلتر کردن آگهی‌های دارای تصویر
  const visibleNotices = useMemo(() => {
    return notices.filter((n) => {
      if (!n.AlbumId) return false;
      const mediaList = mediaMap[String(n.AlbumId)];
      if (mediaList === undefined) return true;
      return mediaList.length > 0;
    });
  }, [notices, mediaMap]);

  // فرمت ابعاد اثر
  const getDimensionsText = (item: any): string | null => {
    if (!item) return null;
    if (item.Dimensions) return String(item.Dimensions);
    const l = item.Length ?? item.length;
    const w = item.Width ?? item.width;
    const h = item.Height ?? item.height;

    const parts = [l, w, h].filter((v) => v !== undefined && v !== null && v !== "");
    if (parts.length > 0) {
      return `${parts.join(" × ")} سانتی‌متر`;
    }
    return null;
  };

  const checkIsDefault = (item: any): boolean => {
    if (!item) return false;
    const val =
      item.isDefault ??
      item.IsDefault ??
      item.isCover ??
      item.IsCover ??
      item.isMain ??
      item.IsMain;
    return (
      val === true ||
      val === 1 ||
      String(val).trim().toLowerCase() === "true" ||
      String(val).trim() === "1"
    );
  };

  const fetchMediaForNotices = useCallback(async (newNotices: NoticeHeaderDto[]) => {
    const albumsToFetch = Array.from(
      new Set(
        newNotices
          .map((n) => (n.AlbumId ? String(n.AlbumId) : null))
          .filter((id): id is string => Boolean(id))
      )
    );

    if (albumsToFetch.length === 0) return;

    const fetchPromises = albumsToFetch.map(async (albumId) => {
      try {
        const mediaList = await getMediaByAlbumId(albumId);
        return { albumId, mediaList: mediaList || [] };
      } catch (error) {
        console.error(`Error loading media for album ${albumId}:`, error);
        return { albumId, mediaList: [] };
      }
    });

    const results = await Promise.all(fetchPromises);

    setMediaMap((prev) => {
      const nextMap = { ...prev };
      results.forEach((res) => {
        nextMap[res.albumId] = res.mediaList;
      });
      return nextMap;
    });
  }, []);

  const fetchNotices = useCallback(
    async (currentSkip: number) => {
      try {
        setLoading(true);
        const data = await getNoticesOData(currentSkip, TOP);

        if (!data || data.length < TOP) {
          setHasMore(false);
        }

        const items = data ? [...data] : [];

        if (currentSkip === 0 && items.length > 2) {
          items.splice(2, 0, {
            ...items[0],
            Id: (`ad-${Date.now()}` as unknown) as number,
            isAd: true,
            Title: "شاهکار منتخب گالری",
            AlbumId: items[0].AlbumId,
          } as any);
        }

        setNotices((prev) => (currentSkip === 0 ? items : [...prev, ...items]));
        fetchMediaForNotices(items);
      } catch (error) {
        console.error("Error fetching notices:", error);
      } finally {
        setLoading(false);
      }
    },
    [fetchMediaForNotices]
  );

  useEffect(() => {
    fetchNotices(0);
  }, [fetchNotices]);

  useEffect(() => {
    if (screens.md && visibleNotices.length > 0) {
      const isCurrentValid = visibleNotices.some((n) => n.Id === selectedNotice?.Id);
      if (!selectedNotice || !isCurrentValid) {
        setSelectedNotice(visibleNotices.find((n: any) => !n.isAd) || visibleNotices[0]);
      }
    }
  }, [screens.md, visibleNotices, selectedNotice]);

  const loadMore = () => {
    const newSkip = skip + TOP;
    setSkip(newSkip);
    fetchNotices(newSkip);
  };

  const handleSelectNotice = (notice: NoticeHeaderDto) => {
    setSelectedNotice(notice);
    // ریست کردن استیت‌های تماس هنگام تغییر اثر
    setShowPhone(false);
    setPhoneNumber(null);
    
    if (!screens.md) {
      setIsMobileDrawerOpen(true);
    } else if (viewMode === "grid") {
      setIsGridModalOpen(true);
    }
  };

  // دریافت شماره تماس با استفاده از CreatedBy آگهی
  const handleContactClick = async () => {
    setShowPhone(true);
    if (phoneNumber) return;

    const creatorId = (selectedNotice as any)?.CreatedBy || (selectedNotice as any)?.createdBy;

    if (!creatorId) {
      message.warning("شناسه کاربر ثبت‌کننده در این آگهی یافت نشد.");
      return;
    }

    setIsPhoneLoading(true);
    try {
      const response = await getUserPhoneNumber(String(creatorId));
      const phone = response?.data || (typeof response === "string" ? response : null);

      if (phone) {
        setPhoneNumber(phone);
      } else {
        message.warning("شماره تماسی برای این کاربر ثبت نشده است.");
      }
    } catch (error) {
      console.error("Error fetching phone number:", error);
      message.error("خطا در دریافت شماره تماس");
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const handleCopyPhone = (num: string) => {
    navigator.clipboard.writeText(num);
    message.success("شماره تماس در کلیپ‌بورد کپی شد");
  };

  const renderCoverThumbnail = (notice: NoticeHeaderDto, size: number = 72) => {
    const albumKey = notice.AlbumId ? String(notice.AlbumId) : null;
    const mediaList = albumKey ? mediaMap[albumKey] : undefined;

    if (albumKey && mediaList === undefined) {
      return (
        <div
          style={{
            width: size,
            height: size,
            minWidth: size,
            borderRadius: token.borderRadiusSM,
            backgroundColor: token.colorFillQuaternary,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="small" />
        </div>
      );
    }

    const coverItem: any = mediaList?.find(checkIsDefault) || mediaList?.[0];
    if (!coverItem) return null;

    const base64Data =
      coverItem.thumbnail || coverItem.Thumbnail || coverItem.medium || coverItem.Medium;
    const mimeType = coverItem?.mimeType || coverItem?.MimeType || "image/webp";

    const imgSrc = base64Data
      ? `data:${mimeType};base64,${base64Data}`
      : `https://picsum.photos/seed/${notice.Id}-thumb/150/150`;

    return (
      <img
        src={imgSrc}
        alt={notice.Title || "کاور اثر"}
        style={{
          width: size,
          height: size,
          minWidth: size,
          objectFit: "cover",
          borderRadius: token.borderRadiusSM,
          border: `1px solid ${token.colorBorderSecondary}`,
        }}
        loading="lazy"
      />
    );
  };

  const renderGallery = (
    notice: NoticeHeaderDto,
    height: number = 220,
    isDetailMode: boolean = false
  ) => {
    const albumKey = notice.AlbumId ? String(notice.AlbumId) : null;
    const mediaList = albumKey ? mediaMap[albumKey] : undefined;

    if (albumKey && mediaList === undefined) {
      return (
        <div
          style={{
            height,
            backgroundColor: token.colorFillQuaternary,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin tip="در حال بارگذاری..." size="small" />
        </div>
      );
    }

    if (!mediaList || mediaList.length === 0) {
      return null;
    }

    const mediaCopy = [...mediaList];
    const defaultIdx = mediaCopy.findIndex(checkIsDefault);
    if (defaultIdx > -1) {
      const [coverItem] = mediaCopy.splice(defaultIdx, 1);
      mediaCopy.unshift(coverItem);
    }

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          height,
          backgroundColor: token.colorFillSecondary,
          overflow: "hidden",
        }}
      >
        <Image.PreviewGroup>
          <Carousel
            key={`${notice.Id}-${isDetailMode ? "detail" : "thumb"}`}
            initialSlide={0}
            arrows={true}
            infinite={false}
            dots={isDetailMode}
            draggable={true}
            style={{ height, overflow: "hidden" }}
          >
            {mediaCopy.map((item: any, idx: number) => {
              const base64Data = isDetailMode
                ? item.medium || item.Medium || item.thumbnail || item.Thumbnail
                : item.thumbnail || item.Thumbnail || item.medium || item.Medium;

              const mimeType = item.mimeType || item.MimeType || "image/webp";
              const imageSrc = `data:${mimeType};base64,${base64Data}`;
              const keyId = item.id || item.Id || `img-${idx}`;

              return (
                <div key={keyId}>
                  <div
                    style={{
                      height,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: token.colorFillAlter,
                    }}
                  >
                    <Image
                      src={imageSrc}
                      alt={notice.Title || "تصویر اثر"}
                      style={{ width: "100%", height, objectFit: "cover" }}
                      preview={isDetailMode}
                    />
                  </div>
                </div>
              );
            })}
          </Carousel>
        </Image.PreviewGroup>

        {!isDetailMode && mediaCopy.length > 1 && (
          <div
            style={{
              position: "absolute",
              bottom: 8,
              insetInlineEnd: 8,
              background: "rgba(0, 0, 0, 0.65)",
              color: "#fff",
              padding: "2px 8px",
              borderRadius: 12,
              fontSize: 11,
              fontWeight: 500,
              backdropFilter: "blur(4px)",
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              gap: 4,
              zIndex: 2,
            }}
          >
            <PictureOutlined /> {mediaCopy.length}
          </div>
        )}
      </div>
    );
  };

  const renderCard = (notice: NoticeHeaderDto, isMasterList: boolean = false) => {
    const isSelected = selectedNotice?.Id === notice.Id;
    const isAd = Boolean((notice as any).isAd);
    const priceFormatted = notice.Price ? `${notice.Price.toLocaleString("fa-IR")} تومان` : "توافقی";
    const dims = getDimensionsText(notice);

    if (isMasterList) {
      const cardContent = (
        <Card
          hoverable
          size="small"
          onClick={() => handleSelectNotice(notice)}
          style={{
            borderRadius: isAd ? 10 : 12,
            overflow: "hidden",
            transition: "all 0.2s ease-in-out",
            border: isAd
              ? "none"
              : `1px solid ${isSelected ? token.colorPrimary : token.colorBorderSecondary}`,
            backgroundColor: isSelected ? token.colorPrimaryBg : token.colorBgContainer,
            boxShadow: isSelected ? `0 4px 12px ${token.colorPrimary}25` : "none",
          }}
          styles={{ body: { padding: 10 } }}
        >
          <Flex gap={12} align="center">
            {renderCoverThumbnail(notice, 72)}

            <div style={{ flex: 1, minWidth: 0 }}>
              <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                <Flex gap={4} align="center">
                  <Tag
                    color={isAd ? "gold" : "blue"}
                    bordered={false}
                    style={{
                      margin: 0,
                      fontSize: 10,
                      lineHeight: "18px",
                      padding: "0 6px",
                      borderRadius: 4,
                    }}
                  >
                    {notice.Category?.LocalizedName || "اثر هنری"}
                  </Tag>
                  {isAd && (
                    <Tag
                      color="warning"
                      bordered={false}
                      style={{
                        margin: 0,
                        fontSize: 9,
                        lineHeight: "18px",
                        padding: "0 4px",
                        borderRadius: 4,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <StarFilled style={{ fontSize: 9 }} /> برگزیده
                    </Tag>
                  )}
                </Flex>
                <Text
                  type="secondary"
                  style={{ fontSize: 10, display: "inline-flex", alignItems: "center", gap: 3 }}
                >
                  <ClockCircleOutlined />
                  {getRelativeTime(notice.CreatedDate)}
                </Text>
              </Flex>

              <Title
                level={5}
                ellipsis={{ rows: 1 }}
                style={{ margin: "0 0 3px 0", fontSize: 13, fontWeight: 600 }}
              >
                {notice.Title || "بدون عنوان"}
              </Title>

              {notice.ArtistName && (
                <Text
                  type="secondary"
                  ellipsis
                  style={{
                    display: "block",
                    fontSize: 11,
                    marginBottom: 4,
                    lineHeight: 1.4,
                  }}
                >
                  <UserOutlined style={{ marginInlineEnd: 4 }} />
                  {notice.ArtistName}
                </Text>
              )}

              <Flex justify="space-between" align="center" style={{ marginTop: 2 }}>
                <Text strong style={{ fontSize: 12, color: token.colorPrimary }}>
                  {priceFormatted}
                </Text>
                {dims && (
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    {dims}
                  </Text>
                )}
              </Flex>
            </div>
          </Flex>
        </Card>
      );

      if (isAd) {
        return <div className="ad-card-gold-wrapper">{cardContent}</div>;
      }
      return cardContent;
    }

    const gridCardContent = (
      <Card
        hoverable
        onClick={() => handleSelectNotice(notice)}
        style={{
          borderRadius: isAd ? 12 : 14,
          overflow: "hidden",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          border: isAd ? "none" : `1px solid ${token.colorBorderSecondary}`,
          backgroundColor: token.colorBgContainer,
          boxShadow: token.boxShadowTertiary,
        }}
        styles={{
          body: {
            padding: 16,
            display: "flex",
            flexDirection: "column",
            flex: 1,
          },
        }}
        cover={renderGallery(notice, 210, false)}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Tag
              color={isAd ? "gold" : "blue"}
              bordered={false}
              style={{
                margin: 0,
                fontSize: 11,
                borderRadius: 6,
                padding: "1px 8px",
                fontWeight: 500,
              }}
            >
              {notice.Category?.LocalizedName || "هنر تجسمی"}
            </Tag>
            {isAd && (
              <Tag
                color="warning"
                bordered={false}
                style={{
                  margin: 0,
                  fontSize: 10,
                  borderRadius: 6,
                  padding: "1px 6px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  fontWeight: 600,
                }}
              >
                <StarFilled style={{ fontSize: 10 }} /> برگزیده
              </Tag>
            )}
          </div>
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <ClockCircleOutlined />
            {getRelativeTime(notice.CreatedDate)}
          </Text>
        </div>

        <Title
          level={5}
          ellipsis={{ rows: 1 }}
          style={{
            margin: "0 0 6px 0",
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {notice.Title || "بدون عنوان"}
        </Title>

        <Paragraph
          ellipsis={{ rows: 2 }}
          type="secondary"
          style={{
            fontSize: 12,
            minHeight: 34,
            marginBottom: 8,
            lineHeight: 1.6,
          }}
        >
          {notice.Description || "توضیحاتی برای این اثر ثبت نشده است."}
        </Paragraph>

        {dims && (
          <div style={{ marginBottom: 10 }}>
            <Tag
              icon={<ColumnWidthOutlined />}
              bordered={false}
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 6,
                background: token.colorFillAlter,
                color: token.colorTextSecondary,
              }}
            >
              {dims}
            </Tag>
          </div>
        )}

        <div
          style={{
            marginTop: "auto",
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {notice.ArtistName ? (
            <Text
              type="secondary"
              ellipsis
              style={{
                fontSize: 12,
                maxWidth: "50%",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <UserOutlined />
              {notice.ArtistName}
            </Text>
          ) : (
            <span />
          )}

          <Text
            strong
            style={{
              fontSize: 13,
              color: token.colorPrimary,
              fontWeight: 700,
            }}
          >
            {priceFormatted}
          </Text>
        </div>
      </Card>
    );

    if (isAd) {
      return <div className="ad-card-gold-wrapper" style={{ height: "100%" }}>{gridCardContent}</div>;
    }

    return gridCardContent;
  };

  const renderNoticeDetails = (notice: NoticeHeaderDto) => {
    const dimensions = getDimensionsText(notice);
    const isAd = Boolean((notice as any).isAd);

    return (
      <div style={{ padding: screens.md ? 24 : 16 }}>
        <div style={{ borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
          {renderGallery(notice, screens.md ? 380 : 250, true)}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Tag color="cyan" bordered={false} style={{ padding: "2px 10px", fontSize: 12 }}>
                {notice.Category?.LocalizedName || "اثر هنری"}
              </Tag>
              {isAd && (
                <Tag
                  color="gold"
                  bordered={false}
                  style={{
                    padding: "2px 8px",
                    fontSize: 12,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <StarFilled /> اثر ویژه منتخب
                </Tag>
              )}
            </div>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <ClockCircleOutlined />
              {getRelativeTime(notice.CreatedDate)}
            </Text>
          </div>

          <Title level={3} style={{ marginTop: 0, marginBottom: 16, fontWeight: 700 }}>
            {notice.Title}
          </Title>

          <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <div
                style={{
                  background: token.colorFillAlter,
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                  قیمت اثر
                </Text>
                <Text strong style={{ fontSize: 14, color: token.colorPrimary }}>
                  {notice.Price ? `${notice.Price.toLocaleString("fa-IR")} تومان` : "توافقی"}
                </Text>
              </div>
            </Col>

            {notice.ArtistName && (
              <Col xs={12} sm={6}>
                <div
                  style={{
                    background: token.colorFillAlter,
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                    پدیدآورنده
                  </Text>
                  <Text
                    strong
                    style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <UserOutlined /> {notice.ArtistName}
                  </Text>
                </div>
              </Col>
            )}

            {(notice as any).Year && (
              <Col xs={12} sm={6}>
                <div
                  style={{
                    background: token.colorFillAlter,
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                    سال خلق
                  </Text>
                  <Text
                    strong
                    style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <CalendarOutlined /> {(notice as any).Year}
                  </Text>
                </div>
              </Col>
            )}

            {dimensions && (
              <Col xs={12} sm={6}>
                <div
                  style={{
                    background: token.colorFillAlter,
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                    ابعاد اثر
                  </Text>
                  <Text
                    strong
                    style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <ColumnWidthOutlined /> {dimensions}
                  </Text>
                </div>
              </Col>
            )}
          </Row>

          {/* ─── بخش اطلاعات تماس و فروشنده ─── */}
          <div
            style={{
              marginBottom: 20,
              padding: "14px 18px",
              background: token.colorFillAlter,
              borderRadius: 12,
              border: `1px dashed ${token.colorPrimaryBorder}`,
            }}
          >
            <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
              <div>
                <Text strong style={{ display: "block", fontSize: 13, marginBottom: 2 }}>
                  اطلاعات تماس با آگهی‌دهنده
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  برای هماهنگی بازدید یا خرید اثر با فروشنده در ارتباط باشید.
                </Text>
              </div>

              {!showPhone ? (
                <Button
                  type="primary"
                  icon={<PhoneOutlined />}
                  onClick={handleContactClick}
                  style={{ borderRadius: 8 }}
                >
                  اطلاعات تماس
                </Button>
              ) : isPhoneLoading ? (
                <Spin size="small" tip="در حال دریافت..." />
              ) : phoneNumber ? (
                <Flex align="center" gap={8}>
                  <Button
                    type="primary"
                    ghost
                    icon={<PhoneOutlined />}
                    href={`tel:${phoneNumber}`}
                    style={{ borderRadius: 8, direction: "ltr", fontWeight: 600 }}
                  >
                    {phoneNumber}
                  </Button>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => handleCopyPhone(phoneNumber)}
                    style={{ borderRadius: 8 }}
                  />
                </Flex>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  شماره‌ای یافت نشد
                </Text>
              )}
            </Flex>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          <Title level={5} style={{ marginBottom: 8 }}>
            درباره این اثر
          </Title>
          <Paragraph
            style={{
              fontSize: 14,
              lineHeight: 1.8,
              color: token.colorTextSecondary,
              whiteSpace: "pre-line",
            }}
          >
            {notice.Description || "توضیحاتی برای این اثر درج نشده است."}
          </Paragraph>

          {(notice as any).Shipping && (
            <div
              style={{
                marginTop: 20,
                padding: "12px 16px",
                borderRadius: 8,
                background: token.colorFillQuaternary,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
              }}
            >
              <EnvironmentOutlined style={{ color: token.colorPrimary }} />
              <Text type="secondary">شرایط تحویل و ارسال:</Text>
              <Text strong>{String((notice as any).Shipping)}</Text>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: screens.md ? 24 : 12, maxWidth: 1600, margin: "0 auto" }}>
      <style>{goldenAnimationStyles}</style>

      {/* هدر بالای صفحه */}
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0, fontWeight: 700 }}>
            گالری آثار هنری
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            مجموعه جدیدترین نقاشی‌ها، خطاطی‌ها و آثار تجسمی ثبت شده
          </Text>
        </div>

        {screens.md && (
          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="masterDetail">
              <UnorderedListOutlined /> سایدبار و جزئیات
            </Radio.Button>
            <Radio.Button value="grid">
              <AppstoreOutlined /> نمایش شبکه‌ای
            </Radio.Button>
          </Radio.Group>
        )}
      </div>

      {/* حالت Master-Detail */}
      {viewMode === "masterDetail" && screens.md ? (
        <Row gutter={[24, 0]} style={{ height: "calc(100vh - 160px)", minHeight: 650 }}>
          <Col md={10} xl={8} style={{ height: "100%", overflowY: "auto", paddingInlineEnd: 10 }}>
            <List
              dataSource={visibleNotices}
              loading={loading && visibleNotices.length === 0}
              renderItem={(item) => (
                <div key={item.Id} style={{ marginBottom: 12 }}>
                  {renderCard(item, true)}
                </div>
              )}
            />
            {hasMore && visibleNotices.length > 0 && (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <Button onClick={loadMore} loading={loading} type="default" block style={{ borderRadius: 8 }}>
                  بارگذاری آثار بیشتر
                </Button>
              </div>
            )}
          </Col>

          <Col md={14} xl={16} style={{ height: "100%", overflowY: "auto" }}>
            <Card
              style={{
                minHeight: "100%",
                borderRadius: 16,
                border: `1px solid ${token.colorBorderSecondary}`,
                backgroundColor: token.colorBgContainer,
              }}
              styles={{ body: { padding: 0 } }}
            >
              {selectedNotice ? (
                renderNoticeDetails(selectedNotice)
              ) : (
                <div
                  style={{
                    height: "100%",
                    minHeight: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Empty description="یک اثر را برای مشاهده جزئیات انتخاب کنید" />
                </div>
              )}
            </Card>
          </Col>
        </Row>
      ) : (
        /* حالت Grid */
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 2,
            md: 3,
            lg: 3,
            xl: 4,
            xxl: 4,
          }}
          dataSource={visibleNotices}
          loading={loading && visibleNotices.length === 0}
          renderItem={(item) => <List.Item key={item.Id}>{renderCard(item, false)}</List.Item>}
          loadMore={
            hasMore && visibleNotices.length > 0 ? (
              <div style={{ textAlign: "center", marginTop: 24, marginBottom: 24 }}>
                <Button
                  onClick={loadMore}
                  loading={loading}
                  type="primary"
                  ghost
                  style={{ borderRadius: 8, minWidth: 200 }}
                >
                  مشاهده آثار بیشتر
                </Button>
              </div>
            ) : null
          }
        />
      )}

      {/* مودال در حالت گرید */}
      <Modal
        open={isGridModalOpen}
        onCancel={() => setIsGridModalOpen(false)}
        footer={null}
        width={880}
        centered
        destroyOnClose
        styles={{ body: { padding: 0, maxHeight: "85vh", overflowY: "auto" } }}
      >
        {selectedNotice && renderNoticeDetails(selectedNotice)}
      </Modal>

      {/* دراور در حالت موبایل */}
      <Drawer
        title={<Text strong>{selectedNotice?.Title || "جزئیات اثر"}</Text>}
        placement="bottom"
        height="90vh"
        onClose={() => setIsMobileDrawerOpen(false)}
        open={isMobileDrawerOpen}
        styles={{
          body: { padding: 0, backgroundColor: token.colorBgContainer },
          header: { borderBottom: `1px solid ${token.colorBorderSecondary}` },
        }}
      >
        {selectedNotice && renderNoticeDetails(selectedNotice)}
      </Drawer>
    </div>
  );
};

export default NoticeList;
